import { FlagOutlined, PlusCircleOutlined, SaveOutlined } from '@ant-design/icons';
import { Button, Checkbox, Col, Form, Input, message, Modal, Row } from 'antd';
import React, { useState } from 'react';
import Helmet from 'react-helmet';
import ZTable, { IFilterItem, ITableRef, IColumnType } from '@/components/ZTable';
import MainContent from '@/layouts/MainContent';
import { useTranslation } from 'react-i18next';
import { ModalWidth, ROW_GUTTER, VALIDATE_REQUIRED } from '@/config';
import BooleanTag from '@/components/app/BooleanTag';
import BaseTicketClassInput from '@/components/FormItems/BaseTicketClassInput';
import ColorPicker from '@/components/FormItems/ColorPicker';
import { clearAllCompanyTicketsCache } from '@/hooks/data/masterDataHook';
import { CompanyTicketClassDto } from '@/__generated';
import { apiSdk } from '@/httpClient';
import { useCanManageFleetTrip } from '@/hooks/useHasPermissions';

const PAGE_TITLE = 'ticketClassesPage_Title';

const columns: IColumnType<CompanyTicketClassDto>[] = [
  {
    title: 'ticketClassesPage_BaseTicketClassName',
    dataIndex: 'baseTicketClassName',
    ellipsis: true,
    defaultSortOrder: 'ascend',
  },
  {
    title: 'ticketClassesPage_Name',
    dataIndex: 'name',
    sorter: true,
    ellipsis: true,
  },
  {
    title: 'ticketClassesPage_SeatColor',
    dataIndex: 'seatColor',
    sorter: true,
    ellipsis: true,
    render: v => <span style={{ color: v }}>{v}</span>,
  },
  {
    title: 'base.IsStandard',
    dataIndex: 'isStandard',
    render: v => <BooleanTag value={v} />,
  },
];

const filters: Array<IFilterItem> = [
  { type: 'SELECT', name: 'ticketClassId', label: 'ticketClassesPage_BaseTicketClassName', customElm: <BaseTicketClassInput /> },
];

const TicketClassesPage = () => {
  const [t] = useTranslation();
  const zTb = React.useRef<ITableRef>();
  const [form] = Form.useForm();
  const [formVisible, setFormVisible] = useState(false);
  const [isNewForm, setIsNewForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const canUpdate = useCanManageFleetTrip();

  const clearCache = () => {
    clearAllCompanyTicketsCache();
  };
  const createEntity = values => {
    setLoading(true);
    apiSdk.CompanyTicketClassService.create({ body: values })
      .then(response => {
        if (response?.success) {
          setFormVisible(false);
          zTb.current?.reload();
          clearCache();
        } else {
          if (response.message) {
            message.error(t(response.message || ''));
          }
        }
      })
      .catch(error => {
        message.error(error);
      })
      .finally(() => {
        setLoading(false);
      });
  };
  const updateEntity = values => {
    setLoading(true);
    apiSdk.CompanyTicketClassService.update({ body: values })
      .then(response => {
        if (response?.success) {
          setFormVisible(false);
          zTb.current?.reload();
          clearCache();
        } else {
          if (response.message) {
            message.error(t(response.message || ''));
          }
        }
      })
      .catch(error => {
        message.error(error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <MainContent
      title={t(PAGE_TITLE)}
      extraBtns={
        canUpdate
          ? [
              <Button
                key="create"
                type="primary"
                className="nta-create-btn"
                icon={<PlusCircleOutlined />}
                onClick={() => {
                  form.resetFields();
                  setIsNewForm(true);
                  setFormVisible(true);
                }}
              >
                {t('createNew')}
              </Button>,
            ]
          : null
      }
      icon={<FlagOutlined style={{ fontSize: 16 }} />}
    >
      <Helmet title={t(PAGE_TITLE)} />
      <div className="ant-layout-content-body">
        <ZTable
          columns={columns}
          primaryKey="id"
          tableRef={zTb}
          url="/fleets/company-ticket-classes/get-company-ticket-classes"
          bordered
          order_by="name"
          order_by_asc={true}
          allowEdit={canUpdate}
          onEdit={record => {
            setIsNewForm(false);
            setFormVisible(true);
            form.setFieldsValue({
              ...record,
            });
          }}
          allowDelete={canUpdate}
          delete_url="/fleets/company-ticket-classes/delete"
          filters={filters}
          onDeleteCompleted={clearCache}
        ></ZTable>
        {formVisible ? (
          <Modal
            visible={formVisible}
            maskClosable={false}
            width={ModalWidth.small}
            className="nta-custom-form"
            title={isNewForm ? t('ticketClassesPage_CreateTicketClass') : t('ticketClassesPage_UpdateTicketClass')}
            forceRender={true}
            okText={t('save')}
            cancelText={t('cancel')}
            okButtonProps={{ icon: <SaveOutlined />, className: 'save-btn', loading: loading }}
            onCancel={() => {
              form.resetFields();
              setFormVisible(false);
            }}
            onOk={() => {
              form
                .validateFields()
                .then(values => {
                  if (values.id) {
                    updateEntity(values);
                  } else {
                    createEntity(values);
                  }
                })
                .catch(info => {});
            }}
          >
            <Form form={form} size="middle" layout="horizontal" labelAlign="right" labelCol={{ md: 10 }}>
              <Form.Item name="id" className="hidden">
                <Input type="hidden" />
              </Form.Item>

              <Row gutter={ROW_GUTTER}>
                <Col md={24}>
                  <Form.Item
                    label={t('ticketClassesPage_BaseTicketClassName')}
                    name="ticketClassId"
                    rules={[{ required: true, message: VALIDATE_REQUIRED }]}
                  >
                    <BaseTicketClassInput />
                  </Form.Item>
                  <Form.Item label={t('ticketClassesPage_Name')} name="name" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                    <Input />
                  </Form.Item>
                  <Form.Item label={t('ticketClassesPage_SeatColor')} name="seatColor" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                    <ColorPicker />
                  </Form.Item>
                  <Form.Item label="   " colon={false} name="isStandard" valuePropName="checked">
                    <Checkbox>{t('base.IsStandard')} </Checkbox>
                  </Form.Item>
                </Col>
                <Col md={12}></Col>
              </Row>
            </Form>
          </Modal>
        ) : null}
      </div>
    </MainContent>
  );
};
export default TicketClassesPage;
